/**
 * =============================================================================
 * AUTOMATIONS CONTAINER - TOP-LEVEL STATE MANAGEMENT
 * =============================================================================
 * 
 * PURPOSE:
 * This is the ROOT component for the entire Automations feature.
 * It serves as the SINGLE SOURCE OF TRUTH for all workflow builder state.
 * 
 * RESPONSIBILITIES:
 * 1. Manages view navigation (library vs builder)
 * 2. Manages selected node state for configuration panels
 * 3. Controls visibility of all panels (config, leads, guardrails)
 * 4. Passes all state and handlers down to child components via props
 * 
 * STATE OWNERSHIP:
 * - currentView: Controls which main view is displayed (library or builder)
 * - selectedNode: ID of the currently selected workflow step (for editing)
 * - showConfigPanel: Controls the right-side configuration panel visibility
 * - showLeadsPanel: Controls the leads monitor panel visibility
 * - showGuardrails: Controls the guardrails modal visibility
 * - configType: The type of the selected node (trigger, email, sms, wait)
 * 
 * DATA FLOW PATTERN:
 * mockData.js → AutomationsContainer (state) → AutomationBuilder → child components
 *                                            ↓
 *                              ConfigPanel (receives configType + handlers)
 * 
 * IMPORTANT FOR DRAG-AND-DROP:
 * - Currently, workflowSteps comes from mockData.js (static)
 * - To enable drag-and-drop, workflowSteps MUST become state in this component
 * - All step additions/reordering will need handlers defined HERE
 * - Consider using useReducer for complex state updates when implementing DnD
 * 
 * TIGHT COUPLING CONCERNS:
 * - workflowSteps is imported statically from mockData (not stateful)
 * - No mechanism exists to add/remove/reorder steps dynamically
 * - Adding new steps will require state management changes in THIS component
 * =============================================================================
 */
import React, { useState, useCallback, useEffect } from 'react';
import { Play, MessageSquare, Clock, Mail } from 'lucide-react';
import { useIsMobile } from '../../hooks/useResponsive';
import AutomationLibrary from './AutomationLibrary';
import AutomationBuilder from './AutomationBuilder';
import GuardrailsModal from './panels/GuardrailsModal';
import TestRunModal from './components/TestRunModal';
import { fetchAutomations, fetchAutomation, toggleAutomationStatus, stopAutomation, runAutomationTest, deleteAutomationById } from '../../features/automations/api/automations.api';
import { createAutomation, updateAutomation } from '../../features/automations/api/automations.api';
import automationService from '../../features/automations/domain/automationService';
import { auth } from '../../firebase/firebase.config';
import { usePortfolioData } from '../capture-tools/hooks/usePortfolioData';
import { usePropertyTabContainer } from '../capture-tools/hooks/usePropertyTabContainer';
import { useAvailableForms } from './panels/hooks';
import { uploadFiles } from '../../firebase/storage.service';
import {
  subscribeToAutomationRuns,
  subscribeToUserRuns,
  getRunStats,
  pauseRun,
  resumeRun,
  stopRun,
  deleteRun,
  formatPauseReason,
  getCurrentStepTitle,
} from '../../firebase/automationRuns.service';
import useAppStore from '../../store/appStore';
import { useSearchParams } from 'react-router-dom';

/**
 * =============================================================================
 * INITIAL WORKFLOW STEPS - Default state for a new workflow
 * =============================================================================
 * 
 * This defines the initial state for workflowSteps.
 * Previously imported from mockData.js, now defined here so it can be
 * used as the initial value for useState.
 * 
 * WHY HERE AND NOT IN mockData.js?
 * - State initialization should be close to where state is managed
 * - Makes it clear this is the "default" workflow structure
 * - Allows mockData.js to remain purely for mock API data
 * 
 * IMPORTANT: The trigger step (index 0) is special:
 * - It CANNOT be moved (always first)
 * - It CANNOT be removed (workflow must have an entry point)
 * - It CANNOT be duplicated (only one trigger per workflow)
 * =============================================================================
 */
const INITIAL_WORKFLOW_STEPS = [
  { id: 'trigger', type: 'trigger', title: 'Start Trigger', subtitle: 'Form Submit on 123 Main', icon: Play },
  { id: 'sms-1', type: 'sms', title: 'Send SMS', subtitle: 'Hi {{First Name}}, thanks for coming...', icon: MessageSquare },
  { id: 'wait-1', type: 'wait', title: 'Wait 1 Day', subtitle: 'Pauses workflow for 1 day', icon: Clock, config: { delaySeconds: 86400, waitUnit: 'days', waitValue: 1 } },
  { id: 'email-1', type: 'email', title: 'Send Email', subtitle: 'Subject: Quick question about...', icon: Mail },
];

/**
 * =============================================================================
 * STEP TYPE CONFIGURATION - Metadata for creating new steps
 * =============================================================================
 * 
 * Maps step types to their default properties.
 * Used by addStep() to create properly structured step objects.
 * 
 * Each entry contains:
 * - icon: Lucide React component for visual display
 * - defaultTitle: Initial title for new steps of this type
 * - defaultSubtitle: Initial description/placeholder text
 * =============================================================================
 */
const STEP_TYPE_CONFIG = {
  email: {
    icon: Mail,
    defaultTitle: 'Send Email',
    defaultSubtitle: 'Configure email content...'
  },
  sms: {
    icon: MessageSquare,
    defaultTitle: 'Send SMS',
    defaultSubtitle: 'Configure SMS message...'
  },
  wait: {
    icon: Clock,
    // Title is dynamically computed from config - see getWaitStepTitle()
    defaultTitle: 'Wait',
    defaultSubtitle: 'Configure delay...'
  }
};

/**
 * =============================================================================
 * HELPER: FORMAT WAIT DURATION - Generates human-readable wait labels
 * =============================================================================
 * 
 * Converts wait step config to a human-readable string like "Wait 1 Day".
 * This is the single source of truth for wait step display labels.
 * 
 * Priority order for deriving display:
 * 1. waitUnit + waitValue (explicit display values)
 * 2. delaySeconds (backward compatibility fallback)
 * 
 * @param {Object} config - Wait step config { delaySeconds, waitUnit, waitValue }
 * @returns {string} Human-readable label like "Wait 1 Day" or "Wait 3 Hours"
 */
const formatWaitDuration = (config) => {
  if (!config) return 'Wait';

  const { waitUnit, waitValue, delaySeconds } = config;

  // Priority 1: Use explicit display values if available
  if (waitUnit && waitValue !== undefined && waitValue !== null) {
    const value = Number(waitValue);
    if (Number.isFinite(value) && value > 0) {
      const unitLabels = {
        days: value === 1 ? 'Day' : 'Days',
        hours: value === 1 ? 'Hour' : 'Hours',
        minutes: value === 1 ? 'Minute' : 'Minutes'
      };
      const label = unitLabels[waitUnit] || waitUnit;
      return `Wait ${value} ${label}`;
    }
  }

  // Priority 2: Derive from delaySeconds (backward compatibility)
  if (delaySeconds && Number.isFinite(Number(delaySeconds))) {
    const seconds = Number(delaySeconds);
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (days > 0 && seconds % 86400 === 0) {
      return `Wait ${days} ${days === 1 ? 'Day' : 'Days'}`;
    }
    if (hours > 0 && seconds % 3600 === 0) {
      return `Wait ${hours} ${hours === 1 ? 'Hour' : 'Hours'}`;
    }
    if (minutes > 0) {
      return `Wait ${minutes} ${minutes === 1 ? 'Minute' : 'Minutes'}`;
    }
    // Very short wait - less than a minute
    return `Wait ${seconds} ${seconds === 1 ? 'Second' : 'Seconds'}`;
  }

  return 'Wait';
};

const AutomationsContainer = () => {
  // ========== RESPONSIVE HOOK ==========
  // Determines mobile vs desktop layout throughout the component tree
  const isMobile = useIsMobile();

  // ========== ZUSTAND STORE SELECTORS ==========
  const cachedAutomations = useAppStore((s) => s.automations);
  const setAutomationsInStore = useAppStore((s) => s.setAutomations);
  const setAutomationRunsInStore = useAppStore((s) => s.setAutomationRuns);

  // ========== URL STATE → VIEW MODE & SELECTED AUTOMATION ==========
  // Uses a single useSearchParams to avoid race conditions when
  // setting both params in the same synchronous block.
  const [searchParams, setSearchParams] = useSearchParams();

  const viewParam = searchParams.get('view') || 'list';
  const automationParam = searchParams.get('automation') || '';

  /**
   * Atomically update one or more URL search params.
   * Prevents one setSearchParams call from overwriting another.
   * @param {Record<string, string | null>} updates - key/value pairs to set (null deletes)
   */
  const setUrlParams = useCallback((updates) => {
    setSearchParams((prev) => {
      Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') {
          prev.delete(key);
        } else {
          prev.set(key, value);
        }
      });
      return prev;
    }, { replace: true });
  }, [setSearchParams]);

  // ========== VIEW NAVIGATION STATE ==========
  // Derived from URL: 'list' in URL maps to internal 'library', 'builder' stays 'builder'
  const currentView = viewParam === 'list' ? 'library' : viewParam;
  const setCurrentView = useCallback((view) => {
    setUrlParams({ view: view === 'library' ? 'list' : view });
  }, [setUrlParams]);

  // ========== LIBRARY TAB STATE ==========
  // Controls which tab is active in the library view:
  // - 'active': Shows active workflows
  // - 'paused': Shows paused/attention-required leads
  const [activeLibTab, setActiveLibTab] = useState('active');

  // ========== WORKFLOW NODE SELECTION STATE ==========
  // Stores the ID of the currently selected workflow step (e.g., 'trigger', 'sms', 'email')
  // When a node is selected, the ConfigPanel opens to allow editing
  // Set to null when no node is selected
  const [selectedNode, setSelectedNode] = useState(null);

  // ========== PANEL VISIBILITY STATE ==========
  // Controls visibility of the right-side configuration panel
  // Opens when a workflow node is clicked, closes when done editing
  const [showConfigPanel, setShowConfigPanel] = useState(false);

  // Controls visibility of the leads monitor panel
  // Shows which leads are currently active in the workflow
  const [showLeadsPanel, setShowLeadsPanel] = useState(false);

  // ========== LEAD MONITOR FILTER STATE ==========
  // Filter values for Lead Monitor panel - applied at Firestore query level
  const [monitorStatusFilter, setMonitorStatusFilter] = useState('all'); // 'all' | status values
  const [monitorSourceFilter, setMonitorSourceFilter] = useState('all'); // 'all' | 'formSubmission' | 'manual'

  // Controls visibility of the guardrails modal
  // Guardrails are safety rules (stop on reply, quiet hours, etc.)
  const [showGuardrails, setShowGuardrails] = useState(false);

  // Controls visibility of the test run modal
  const [showTestRunModal, setShowTestRunModal] = useState(false);

  // ========== GUARDRAILS STATE ==========
  // Stores the current guardrails configuration for the automation
  // Loaded from Firestore when editing, defaults if missing
  const [guardrails, setGuardrails] = useState({
    stopOnReply: true,
    stopOnStatusChange: {
      enabled: true,
      statuses: ['closed', 'archived']
    },
    stopOnWeekend: false
  });

  // ========== CONFIG PANEL TYPE STATE ==========
  // Stores the type of the selected node to render the correct config UI
  // Possible values: 'trigger', 'sms', 'email', 'wait', null
  // Used by ConfigPanel to switch between different configuration forms
  const [configType, setConfigType] = useState(null);

  // =============================================================================
  // WORKFLOW STEPS STATE - THE CORE DATA FOR DRAG-AND-DROP
  // =============================================================================
  // 
  // WHY STATE LIVES HERE (AutomationsContainer):
  // 1. Single Source of Truth: All workflow modifications go through one place
  // 2. Prop Drilling Pattern: State flows down, events flow up
  // 3. Sibling Communication: ToolsBar and WorkflowStack need to share data
  // 4. Persistence Ready: Easy to add save/load logic at this level
  // 
  // This array represents the current workflow sequence.
  // - Index 0 is ALWAYS the trigger (entry point)
  // - Subsequent indices are action steps in execution order
  // - Modifying this array triggers re-render of WorkflowStack
  // =============================================================================
  const [workflowSteps, setWorkflowSteps] = useState([]);

  // =============================================================================
  // HELPER FUNCTION: GET STEP SUBTITLE
  // =============================================================================
  // Derives subtitle from step.config for Firestore steps
  // Shows actual configured values instead of generic placeholders
  const getStepSubtitle = useCallback((step) => {
    // If step already has explicit subtitle, use it (except for wait steps)
    if (step.subtitle && step.type !== 'wait') return step.subtitle;

    const { config, type } = step;
    if (!config) {
      // No config yet - show placeholder
      const defaults = STEP_TYPE_CONFIG[type] || {};
      return defaults.defaultSubtitle || '';
    }

    // Derive subtitle from config based on step type
    switch (type) {
      case 'email':
        if (config.subject) return `Subject: ${config.subject}`;
        if (config.to) return `To: ${config.to}`;
        return 'Configure email content...';
      case 'sms':
        if (config.body || config.message) {
          const body = config.body || config.message;
          const preview = body.substring(0, 40);
          return preview.length < body.length ? `${preview}...` : preview;
        }
        if (config.to) return `To: ${config.to}`;
        return 'Configure SMS message...';
      case 'wait':
        // For wait steps, show configured duration
        if (config.waitValue && config.waitUnit) {
          return `Wait ${config.waitValue} ${config.waitUnit}`;
        }
        return 'Configure wait duration...';
      case 'webhook':
        if (config.url) return config.url.substring(0, 40) + '...';
        return 'Configure webhook...';
      default:
        return '';
    }
  }, []);

  // =============================================================================
  // HELPER FUNCTION: GET STEP TITLE
  // =============================================================================
  // Derives title from step.config for Firestore steps
  // For wait steps, this computes a human-readable duration label
  const getStepTitle = useCallback((step) => {
    const { config, type } = step;
    const typeConfig = STEP_TYPE_CONFIG[type] || {};

    // Wait steps get dynamic titles based on configuration
    if (type === 'wait' && config) {
      return formatWaitDuration(config);
    }

    // Other step types use default or explicit title
    return step.title || typeConfig.defaultTitle || type;
  }, []);

  // =============================================================================
  // HELPER FUNCTION: HYDRATE STEPS
  // =============================================================================
  // Firestore steps only have { id, type, config }
  // UI needs { id, type, config, title, subtitle, icon }
  // This function adds display properties based on step type and config
  const hydrateSteps = (steps) => {
    return steps.map(step => {
      const typeConfig = STEP_TYPE_CONFIG[step.type] || {};
      return {
        ...step,
        title: getStepTitle(step),
        subtitle: getStepSubtitle(step),
        icon: typeConfig.icon,
      };
    });
  };

  // =============================================================================
  // HELPER FUNCTION: ENSURE TRIGGER EXISTS
  // =============================================================================
  // Ensures the workflow always has a trigger step at index 0
  // This prevents bugs where the trigger gets removed or lost
  const ensureTriggerExists = (steps, automation = null) => {
    if (steps.length === 0 || steps[0]?.type !== 'trigger') {
      const triggerStep = {
        id: 'trigger',
        type: 'trigger',
        title: 'Start Trigger',
        subtitle: automation?.trigger?.type ?
          `Trigger: ${automation.trigger.type}` :
          'No trigger configured',
        icon: Play,
        config: automation?.trigger?.config || {}
      };
      return [triggerStep, ...steps];
    }
    return steps;
  };

  // =============================================================================
  // STEP ID COUNTER - Ensures unique IDs for new steps
  // =============================================================================
  // 
  // Tracks the next available ID number for each step type.
  // Format: `${type}-${counter}` (e.g., 'email-2', 'sms-3')
  // 
  // WHY USE A COUNTER INSTEAD OF UUID?
  // - Readable IDs make debugging easier
  // - Predictable format for testing
  // - Lower overhead than UUID generation
  // 
  // NOTE: In production, consider:
  // - UUIDs for globally unique IDs across devices
  // - Server-generated IDs for persistence
  // =============================================================================
  // Workflows list (library) loaded via API stub
  const [workflows, setWorkflows] = useState([]);
  const [isLoadingWorkflows, setIsLoadingWorkflows] = useState(true);
  const [currentAutomation, setCurrentAutomation] = useState(null);
  // automationId derived from URL state (defined above via useUrlState)
  const automationId = automationParam || null;
  const setAutomationId = useCallback((id) => {
    setUrlParams({ automation: id || '' });
  }, [setUrlParams]);
  const [isSaving, setIsSaving] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // ========== READ-ONLY MODE ==========
  // When automation is ACTIVE, the builder becomes view-only to prevent
  // modifications while executions are running.
  const isReadOnly = currentAutomation?.status === 'ACTIVE';

  // Toast state for read-only blocked interactions
  const [readOnlyToast, setReadOnlyToast] = useState(false);
  const readOnlyToastTimerRef = React.useRef(null);

  const showReadOnlyToast = useCallback(() => {
    setReadOnlyToast(true);
    if (readOnlyToastTimerRef.current) clearTimeout(readOnlyToastTimerRef.current);
    readOnlyToastTimerRef.current = setTimeout(() => setReadOnlyToast(false), 3000);
  }, []);

  // Cleanup toast timer on unmount
  useEffect(() => {
    return () => {
      if (readOnlyToastTimerRef.current) clearTimeout(readOnlyToastTimerRef.current);
    };
  }, []);

  // Sync local workflows state to Zustand store whenever it changes.
  // This replaces inline setAutomationsInStore calls inside setWorkflows updaters,
  // which caused "Cannot update a component while rendering a different component".
  useEffect(() => {
    if (workflows.length > 0 || !isLoadingWorkflows) {
      setAutomationsInStore(workflows);
    }
  }, [workflows, isLoadingWorkflows, setAutomationsInStore]);

  // =============================================================================
  // LEADS STATE - For Add Manually trigger configuration
  // =============================================================================
  // Read leads from centralized Zustand store (populated by realtimeSubscriptions)
  const storeLeads = useAppStore((s) => s.leads);
  const leads = storeLeads || [];
  const leadsLoading = storeLeads === null;

  // =============================================================================
  // AUTOMATION RUNS STATE - For Lead Monitor Panel
  // =============================================================================
  // Real-time subscription to automation execution runs
  const [automationRuns, setAutomationRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [runStats, setRunStats] = useState({
    total: 0,
    running: 0,
    paused: 0,
    completed: 0,
    failed: 0,
  });

  // =============================================================================
  // ATTENTION REQUIRED STATE - Paused/Rejected runs across all automations
  // =============================================================================
  const [attentionRuns, setAttentionRuns] = useState([]);
  const [attentionLoading, setAttentionLoading] = useState(true);
  const [attentionFilter, setAttentionFilter] = useState('all');
  const [libraryRuns, setLibraryRuns] = useState([]);

  // Trigger configuration state (persisted with automation)
  const [triggerConfig, setTriggerConfig] = useState({
    entryType: 'form',
    formId: null,
    leadFilters: { tags: [], status: 'All' },
    selectedLeadIds: [],
  });

  // =============================================================================
  // ATTENTION REQUIRED SUBSCRIPTION - Paused/Rejected across all automations
  // =============================================================================
  useEffect(() => {
    const userId = auth.currentUser?.uid;
    if (!userId) {
      setAttentionRuns([]);
      setAttentionLoading(false);
      return;
    }

    setAttentionLoading(true);

    // Subscribe to ALL user runs and filter client-side for paused+rejected
    // (subscribeToUserRuns only supports single status filter)
    const unsubscribe = subscribeToUserRuns((allRuns) => {
      setLibraryRuns(allRuns);
      const pausedOrRejected = allRuns.filter(
        (r) => r.status === 'paused' || r.status === 'rejected'
      );
      setAttentionRuns(pausedOrRejected);
      setAttentionLoading(false);
    }, { limit: 200 });

    return () => unsubscribe();
  }, []);

  // =============================================================================
  // ATTENTION REQUIRED - Normalize runs into display rows
  // =============================================================================
  const normalizedAttentionRows = React.useMemo(() => {
    // Build lookup maps
    const leadsMap = {};
    leads.forEach((l) => { leadsMap[l.id] = l; });

    const automationsMap = {};
    workflows.forEach((w) => { automationsMap[w.id] = w; });

    return attentionRuns.map((run) => {
      // Resolve lead name — prefer live leads, fall back to snapshot
      const liveLead = leadsMap[run.leadId];
      const leadName = liveLead?.name
        || liveLead?.fullName
        || run.leadSnapshot?.name
        || run.leadSnapshot?.fullName
        || 'Unknown Lead';
      const leadEmail = liveLead?.email || run.leadSnapshot?.email || '';

      // Resolve automation/workflow name
      const liveAutomation = automationsMap[run.automationId];
      const workflowName = liveAutomation?.name
        || run.automationSnapshot?.name
        || 'Unknown Workflow';

      // Current step
      const stepTitle = getCurrentStepTitle(run);

      // Reason display
      const reason = formatPauseReason(run.pauseReason, run.pauseDetails);

      // Date — use pausedAt, stoppedAt, or updatedAt
      const rawDate = run.pausedAt || run.stoppedAt || run.updatedAt;
      let dateDisplay = '';
      if (rawDate) {
        const d = rawDate instanceof Date ? rawDate : rawDate.toDate?.() || new Date(rawDate);
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yy = String(d.getFullYear()).slice(-2);
        dateDisplay = `${dd}/${mm}/${yy}`;
      }

      return {
        id: run.id,
        leadId: run.leadId,
        automationId: run.automationId,
        name: leadName,
        email: leadEmail,
        workflow: workflowName,
        step: stepTitle,
        reason,
        pauseReason: run.pauseReason || '',
        time: dateDisplay,
        status: run.status,
      };
    });
  }, [attentionRuns, leads, workflows]);

  // Apply filter on normalized rows
  const filteredAttentionRows = React.useMemo(() => {
    if (attentionFilter === 'all') return normalizedAttentionRows;

    return normalizedAttentionRows.filter((row) => {
      switch (attentionFilter) {
        // Execution-state filters
        case 'paused_only':
          return (row.status || '').toLowerCase() === 'paused';
        case 'stopped_only':
          return (row.status || '').toLowerCase() === 'rejected';
        // Reason-based filters
        case 'lead_replied':
          return row.pauseReason === 'lead_replied';
        case 'manual':
          return row.pauseReason === 'manual' || row.pauseReason === 'automation_stopped';
        case 'status_change':
          return row.pauseReason === 'status_change';
        case 'no_property':
          return row.pauseReason === 'no_interested_property';
        default:
          return true;
      }
    });
  }, [normalizedAttentionRows, attentionFilter]);

  // =============================================================================
  // LIBRARY CARD METRICS - LEADS + COMPLETED per workflow
  // =============================================================================
  const workflowMetricsMap = React.useMemo(() => {
    const metricsByAutomation = new Map();

    libraryRuns.forEach((run) => {
      const automationKey = run.automationId;
      if (!automationKey) return;

      if (!metricsByAutomation.has(automationKey)) {
        metricsByAutomation.set(automationKey, {
          leadIds: new Set(),
          completedLeadIds: new Set(),
        });
      }

      const item = metricsByAutomation.get(automationKey);
      const leadId = run.leadId || run.leadSnapshot?.id;
      if (leadId) {
        item.leadIds.add(leadId);
        if ((run.status || '').toLowerCase() === 'completed') {
          item.completedLeadIds.add(leadId);
        }
      }
    });

    const result = {};
    metricsByAutomation.forEach((value, key) => {
      result[key] = {
        leadCount: value.leadIds.size,
        completedCount: value.completedLeadIds.size,
      };
    });
    return result;
  }, [libraryRuns]);

  // =============================================================================
  // AUTOMATION RUNS SUBSCRIPTION
  // =============================================================================
  // Subscribe to real-time execution runs when viewing a specific automation
  // This powers the Lead Monitor panel with live data
  useEffect(() => {
    // Only subscribe when we have a specific automation open in builder view
    if (currentView !== 'builder' || !automationId) {
      setAutomationRuns([]);
      setRunStats({ total: 0, running: 0, paused: 0, completed: 0, failed: 0 });
      return;
    }

    // Initialize from Zustand cache for instant render while subscription reconnects
    const cachedRuns = useAppStore.getState().automationRuns;
    const hasCachedRuns = Array.isArray(cachedRuns) && cachedRuns.length > 0;
    if (hasCachedRuns) {
      setAutomationRuns(cachedRuns);
    }
    setRunsLoading(!hasCachedRuns);

    // Build query options with filters
    const queryOptions = { limit: 100 };

    // Apply status filter (convert display status to Firestore status)
    if (monitorStatusFilter && monitorStatusFilter !== 'all') {
      // Map display status to Firestore status values
      const statusMap = {
        'Running': 'running',
        'Waiting': 'waiting',
        'Paused': 'paused',
        'Completed': 'completed',
        'Stopped': 'rejected',
      };
      queryOptions.status = statusMap[monitorStatusFilter] || monitorStatusFilter.toLowerCase();
    }

    // Apply source filter (trigger.type)
    if (monitorSourceFilter && monitorSourceFilter !== 'all') {
      // Map UI source to Firestore trigger.type values
      const sourceMap = {
        'Form': 'FORM_SUBMIT',
        'Manual': 'MANUAL_ENTRY',
      };
      queryOptions.triggerType = sourceMap[monitorSourceFilter] || monitorSourceFilter;
    }

    // Subscribe to runs for this automation with filters
    const unsubscribe = subscribeToAutomationRuns(
      automationId,
      (runs) => {
        setAutomationRuns(runs);
        setAutomationRunsInStore(runs); // write-through to Zustand
        setRunsLoading(false);

        // Compute stats from runs (filtered view stats)
        const stats = { total: 0, running: 0, waiting: 0, paused: 0, completed: 0, failed: 0, rejected: 0 };
        runs.forEach(run => {
          stats.total++;
          const status = (run.status || '').toLowerCase();
          if (status === 'running') stats.running++;
          else if (status === 'waiting') stats.waiting++;
          else if (status === 'paused') stats.paused++;
          else if (status === 'completed') stats.completed++;
          else if (status === 'failed') stats.failed++;
          else if (status === 'rejected') stats.rejected++;
        });
        setRunStats(stats);
      },
      queryOptions
    );

    return () => unsubscribe();
  }, [currentView, automationId, monitorStatusFilter, monitorSourceFilter]);

  // Load automations list — read from Zustand store (populated by realtimeSubscriptions)
  // Automations use one-shot fetch, so we still fetch if store is empty
  useEffect(() => {
    // If Zustand store already has automations data, use it
    if (cachedAutomations !== null) {
      setWorkflows(cachedAutomations);
      setIsLoadingWorkflows(false);
      return;
    }

    let mounted = true;
    setIsLoadingWorkflows(true);
    fetchAutomations()
      .then(data => {
        if (mounted) {
          const list = data || [];
          setWorkflows(list);
          setAutomationsInStore(list);
          setIsLoadingWorkflows(false);
        }
      })
      .catch(() => {
        if (mounted) {
          setWorkflows([]);
          setIsLoadingWorkflows(false);
        }
      });
    return () => { mounted = false; };
  }, [cachedAutomations]); // eslint-disable-line

  // ========== PORTFOLIO & PROPERTIES DATA FOR FORMS ==========
  // Load portfolio and properties to populate form dropdown in "Connect to Form" trigger
  const portfolio = usePortfolioData();
  const propertyTab = usePropertyTabContainer();
  const availableForms = useAvailableForms(portfolio, propertyTab.properties);


  // ========== NODE SELECTION HANDLER ==========
  // Called when a workflow node is clicked in the WorkflowStack
  // Opens the ConfigPanel with the appropriate configuration form
  // 
  // IMPORTANT FOR DRAG-AND-DROP:
  // This handler only handles SELECTION, not reordering.
  // A separate handler will be needed for drag-and-drop operations.
  const handleNodeClick = (type, nodeId) => {
    // Update state to reflect which node is selected
    setSelectedNode(nodeId);
    // Store the node type so ConfigPanel knows which form to render
    setConfigType(type);
    // Open the configuration panel
    setShowConfigPanel(true);
  };

  // Derive the currently selected step object for config panel
  const selectedStep = workflowSteps.find(step => step.id === selectedNode) || null;

  // ========== CLOSE CONFIG PANEL HANDLER ==========
  // Resets all selection state when user is done configuring a node
  const closeConfig = () => {
    setShowConfigPanel(false);
    setSelectedNode(null);  // Deselect the node
    setConfigType(null);    // Clear the config type
  };

  // ========== VIEW NAVIGATION HANDLERS ==========
  // These handlers switch between library and builder views

  // Opens builder with a new workflow
  const handleCreateNew = async () => {
    // Prevent duplicate creation from rapid clicks
    if (isCreating) return;

    setIsCreating(true);
    try {
      // Create a new automation draft with backend-compatible trigger type
      const newAutomation = await createAutomation({
        name: 'New Automation',
        trigger: { type: 'FORM_SUBMIT', config: {} },
        steps: [],
        status: 'DRAFT'
      });

      console.log('[AutomationsContainer] createAutomation result', { id: newAutomation?.id, name: newAutomation?.name });

      // Set the current automation and switch to builder
      setCurrentAutomation(newAutomation);
      console.log('[AutomationsContainer] setCurrentAutomation (new)', newAutomation?.id);
      setAutomationId(newAutomation.id);
      setWorkflowSteps(INITIAL_WORKFLOW_STEPS);

      // Reset trigger config to clean defaults for the new automation
      setTriggerConfig({
        entryType: 'form',
        formId: null,
        leadFilters: { tags: [], status: 'All' },
        selectedLeadIds: [],
      });

      // Set both URL params atomically (view + automation)
      setUrlParams({ view: 'builder', automation: newAutomation.id });
    } catch (error) {
      console.error('Failed to create new automation:', error);
      // TODO: Show error toast
    } finally {
      setIsCreating(false);
    }
  };

  // Opens builder to edit an existing workflow
  const handleCardClick = async (workflow) => {
    try {
      // Load the full automation data
      const fullAutomation = await fetchAutomation(workflow.id);
      console.log('[AutomationsContainer] fetchAutomation result', { id: fullAutomation?.id, name: fullAutomation?.name });

      // Set the automation ID so save updates the correct document
      setAutomationId(fullAutomation.id);

      // Set the current automation and load its steps
      setCurrentAutomation(fullAutomation);
      console.log('[AutomationsContainer] setCurrentAutomation', fullAutomation?.id);

      // Hydrate triggerConfig from loaded automation (for Add Manually selections)
      const loadedTriggerConfig = fullAutomation?.trigger?.config || {};
      setTriggerConfig({
        entryType: loadedTriggerConfig.entryType || 'form',
        formId: loadedTriggerConfig.formId || null,
        leadFilters: loadedTriggerConfig.leadFilters || { tags: [], status: 'All' },
        selectedLeadIds: loadedTriggerConfig.selectedLeadIds || [],
      });

      // Hydrate guardrails from loaded automation (with defaults if missing)
      const loadedGuardrails = fullAutomation?.guardrails || {};
      setGuardrails({
        stopOnReply: loadedGuardrails.stopOnReply ?? true,
        stopOnStatusChange: {
          enabled: loadedGuardrails.stopOnStatusChange?.enabled ?? true,
          statuses: loadedGuardrails.stopOnStatusChange?.statuses || ['closed', 'archived']
        },
        stopOnWeekend: loadedGuardrails.stopOnWeekend ?? false
      });

      // Hydrate steps with display properties (title, subtitle, icon)
      const hydratedSteps = hydrateSteps(fullAutomation.steps || []);
      setWorkflowSteps(ensureTriggerExists(hydratedSteps, fullAutomation));
      // Set both URL params atomically (view + automation)
      setUrlParams({ view: 'builder', automation: fullAutomation.id });
    } catch (error) {
      console.error('Failed to load automation:', error);
      // TODO: Show error toast
    }
  };

  // Returns to the library view (workflow list)
  // Refreshes workflows list to ensure latest data from Firestore
  const handleBack = () => {
    // Clear both URL params atomically
    setUrlParams({ view: 'list', automation: '' });
    setCurrentAutomation(null);

    // Reset trigger config so stale selections don't leak into the next automation
    setTriggerConfig({
      entryType: 'form',
      formId: null,
      leadFilters: { tags: [], status: 'All' },
      selectedLeadIds: [],
    });

    // Refresh workflows list to show any saved changes
    fetchAutomations().then(data => {
      const list = data || [];
      setWorkflows(list);
      setAutomationsInStore(list);
    });
  };

  // ========== NAME CHANGE HANDLER ==========
  // Updates automation name in currentAutomation state
  // Called by BuilderHeader when user edits the name input
  const handleNameChange = useCallback((newName) => {
    if (isReadOnly) { showReadOnlyToast(); return; }
    setCurrentAutomation(prev => prev ? { ...prev, name: newName } : prev);
  }, [isReadOnly, showReadOnlyToast]);

  // ========== PANEL TOGGLE HANDLERS ==========
  // Toggle the leads monitor panel visibility
  const handleToggleLeadsPanel = () => {
    setShowLeadsPanel(!showLeadsPanel);
  };

  // Explicitly close the leads panel
  const handleCloseLeadsPanel = () => {
    setShowLeadsPanel(false);
  };

  // =============================================================================
  // AUTOMATION RUN ACTIONS - Pause/Resume/Stop/Delete execution runs
  // =============================================================================
  // Pause a running or waiting automation run
  const handlePauseRun = useCallback(async (runId) => {
    if (!runId) return;
    try {
      await pauseRun(runId);
      // Subscription will automatically update the UI
    } catch (error) {
      console.error('Failed to pause run:', error);
      // TODO: Show error toast
    }
  }, []);

  // Resume a paused automation run
  const handleResumeRun = useCallback(async (runId) => {
    if (!runId) return;
    try {
      await resumeRun(runId);
      // Subscription will automatically update the UI
    } catch (error) {
      console.error('Failed to resume run:', error);
      // TODO: Show error toast
    }
  }, []);

  // Stop a running automation run
  const handleStopRun = useCallback(async (runId) => {
    if (!runId) return;
    try {
      await stopRun(runId, 'manual');
      // Subscription will automatically update the UI
    } catch (error) {
      console.error('Failed to stop run:', error);
      // TODO: Show error toast
    }
  }, []);

  // Delete an automation execution run
  const handleDeleteRun = useCallback(async (runId) => {
    if (!runId) return;
    try {
      await deleteRun(runId);
      // Subscription will automatically update the UI
    } catch (error) {
      console.error('Failed to delete run:', error);
      // TODO: Show error toast
    }
  }, []);

  // =============================================================================
  // AUTOMATION DELETE - Delete entire automation with confirmation
  // =============================================================================
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [automationToDelete, setAutomationToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleRequestDeleteAutomation = useCallback((automationId) => {
    setAutomationToDelete(automationId);
    setShowDeleteConfirm(true);
  }, []);

  const handleConfirmDeleteAutomation = useCallback(async () => {
    if (!automationToDelete) return;
    setIsDeleting(true);
    try {
      await deleteAutomationById(automationToDelete);
      // Refresh workflows list
      const data = await fetchAutomations();
      const list = data || [];
      setWorkflows(list);
      setAutomationsInStore(list);
      setShowDeleteConfirm(false);
      setAutomationToDelete(null);
      // If we're in the builder for this automation, go back to library
      if (automationId === automationToDelete) {
        setUrlParams({ view: 'list', automation: '' });
        setCurrentAutomation(null);
      }
    } catch (error) {
      console.error('Failed to delete automation:', error);
      // TODO: Show error toast
    } finally {
      setIsDeleting(false);
    }
  }, [automationToDelete, automationId]);

  const handleCancelDeleteAutomation = useCallback(() => {
    setShowDeleteConfirm(false);
    setAutomationToDelete(null);
  }, []);

  // Open the guardrails modal
  const handleShowGuardrails = () => {
    setShowGuardrails(true);
  };

  // Close the guardrails modal
  const handleCloseGuardrails = () => {
    setShowGuardrails(false);
  };

  // Save guardrails to Firestore
  const handleSaveGuardrails = async (updatedGuardrails) => {
    if (isReadOnly) { showReadOnlyToast(); return; }
    if (!automationId) {
      console.error('No automation ID to save guardrails');
      return;
    }

    try {
      await updateAutomation(automationId, { guardrails: updatedGuardrails });
      setGuardrails(updatedGuardrails); // Update local state
      setShowGuardrails(false);
      // TODO: Show success toast
    } catch (error) {
      console.error('Failed to save guardrails:', error);
      // TODO: Show error toast
    }
  };

  // =============================================================================
  // STEP MANIPULATION HANDLERS
  // =============================================================================
  // 
  // These handlers provide the API for modifying the workflow.
  // They are passed down to child components and will be called by
  // drag-and-drop operations in the next implementation phase.
  // 
  // DESIGN PRINCIPLES:
  // - Pure functions: No side effects, predictable outputs
  // - Immutability: Always create new arrays, never mutate
  // - Validation: Protect trigger step from invalid operations
  // - useCallback: Stable references prevent unnecessary re-renders
  // =============================================================================

  /**
   * ADD STEP - Inserts a new step at the specified position
   * 
   * @param {string} type - The step type: 'email' | 'sms' | 'wait'
   * @param {number} index - Position to insert (1-based, since 0 is trigger)
   * 
   * VALIDATION RULES:
   * - Cannot add 'trigger' type (only one trigger allowed)
   * - Cannot insert at index 0 (trigger position)
   * - Index is clamped to valid range [1, length]
   * 
   * EXAMPLE:
   * addStep('email', 2) with steps [trigger, sms, wait]
   * Results in: [trigger, sms, NEW_EMAIL, wait]
   * 
   * CALLED BY (future):
   * - ToolsBar drag-to-canvas drop handler
   * - "Add Step" button (if implemented)
   */
  const addStep = useCallback((type, index) => {
    // GUARD: Read-only mode — automation is ACTIVE
    if (isReadOnly) { showReadOnlyToast(); return; }

    // GUARD: Prevent adding trigger steps
    if (type === 'trigger') {
      console.warn('Cannot add trigger step - workflow can only have one trigger');
      return;
    }

    // GUARD: Prevent inserting at position 0 (trigger's position)
    if (index < 1) {
      console.warn('Cannot insert step before trigger - using index 1');
      index = 1;
    }

    // Get configuration for this step type
    const config = STEP_TYPE_CONFIG[type];
    if (!config) {
      console.error(`Unknown step type: ${type}`);
      return;
    }

    // Use domain helper to create backend-ready default step data (id + config)
    const backendStep = automationService.getDefaultStep(type);

    // Create the new step object (include UI fields for rendering)
    const newStep = {
      id: backendStep.id,
      type: type,
      title: config.defaultTitle,
      subtitle: config.defaultSubtitle,
      icon: config.icon,
      config: backendStep.config
    };

    // Update state immutably
    setWorkflowSteps(prevSteps => {
      // Clamp index to valid range
      const insertIndex = Math.min(index, prevSteps.length);

      // Create new array with step inserted
      const newSteps = [...prevSteps];
      newSteps.splice(insertIndex, 0, newStep);
      return newSteps;
    });
  }, [isReadOnly, showReadOnlyToast]);

  // ========== SAVE / UPDATE HANDLER ==========
  // Uses automationService.prepareAutomationForSave to create a backend-ready
  // payload, then calls createAutomation or updateAutomation from the API stubs.
  const saveAutomation = useCallback(async ({ showSavingIndicator = true } = {}) => {
    // GUARD: Read-only mode — automation is ACTIVE
    if (isReadOnly) { showReadOnlyToast(); return null; }

    if (showSavingIndicator) {
      setIsSaving(true);
    }
    setSaveError(null);

    try {
      // Build a minimal trigger object from the first step
      // Merge with triggerConfig state to include Add Manually selections
      const triggerStep = workflowSteps[0];

      // CRITICAL: trigger.type must be the BACKEND trigger type ('FORM_SUBMIT' or 'MANUAL_ENTRY'),
      // NOT the UI step type ('trigger'). triggerStep.type is always 'trigger' because it's
      // a workflow node type. The actual backend trigger type is derived from entryType.
      const resolvedTriggerType = triggerConfig.entryType === 'manual' ? 'MANUAL_ENTRY' : 'FORM_SUBMIT';

      const trigger = triggerStep ? {
        id: triggerStep.id,
        type: resolvedTriggerType,
        config: {
          ...(triggerStep.config || {}),
          ...triggerConfig, // Include Add Manually selections (entryType, leadFilters, selectedLeadIds)
        }
      } : null;

      console.log('[saveAutomation] triggerConfig state:', triggerConfig);
      console.log('[saveAutomation] workflowSteps[0].config:', triggerStep?.config);
      console.log('[saveAutomation] merged trigger.config:', trigger?.config);
      console.log('[saveAutomation] selectedLeadIds:', trigger?.config?.selectedLeadIds);

      // Steps excluding trigger
      const steps = workflowSteps.slice(1);

      const automationCandidate = {
        id: automationId || null,
        name: currentAutomation?.name || 'Untitled Automation',
        description: currentAutomation?.description || '',
        status: 'DRAFT',
        trigger,
        steps,
        guardrails
      };

      const payload = automationService.prepareAutomationForSave(automationCandidate);

      let result;
      if (!automationId) {
        // Create new automation
        result = await createAutomation(payload);
        if (result && result.id) setAutomationId(result.id);
      } else {
        // Update existing automation
        result = await updateAutomation(automationId, payload);
      }

      // Refresh workflows list to show updated data
      fetchAutomations().then(data => {
        const list = data || [];
        setWorkflows(list);
        setAutomationsInStore(list);
      });

      // Success: no UI changes required; consumer can inspect returned result
      return result;
    } catch (err) {
      console.error('Failed to save automation', err);
      setSaveError(err);
      return null;
    } finally {
      if (showSavingIndicator) {
        setIsSaving(false);
      }
    }
  }, [automationId, workflowSteps, currentAutomation, triggerConfig, guardrails, isReadOnly, showReadOnlyToast]);

  // ========== NODE-LEVEL SAVE HANDLER ==========
  // Persists a single node's config to Firestore immediately (no schema changes)
  const handleSaveNodeConfig = useCallback(async (stepId, newConfig) => {
    // GUARD: Read-only mode — automation is ACTIVE
    if (isReadOnly) { showReadOnlyToast(); return; }

    if (!automationId) {
      throw new Error('Automation must be saved before updating steps.');
    }
    if (!stepId) {
      throw new Error('Step ID is required to update configuration.');
    }

    const targetStep = workflowSteps.find(step => step.id === stepId);
    if (!targetStep) {
      throw new Error('Selected step not found.');
    }
    if (targetStep.type === 'trigger') {
      throw new Error('Trigger configuration is not saved via node Done.');
    }

    // ========== UPLOAD PENDING ATTACHMENTS (email steps only) ==========
    let finalConfig = { ...newConfig };
    if (targetStep.type === 'email' && Array.isArray(finalConfig.attachments)) {
      const userId = auth.currentUser?.uid;
      const pendingFiles = finalConfig.attachments.filter(a => a.status === 'pending' && a.file);

      if (pendingFiles.length > 0 && userId) {
        try {
          const rawFiles = pendingFiles.map(a => a.file);
          const uploaded = await uploadFiles({
            userId,
            scopeType: 'automations',
            scopeId: automationId,
            messageId: stepId,
            files: rawFiles,
          });

          // Build a map of pending IDs → uploaded metadata
          const uploadMap = new Map();
          pendingFiles.forEach((pending, idx) => {
            if (uploaded[idx]) {
              uploadMap.set(pending.id, uploaded[idx]);
            }
          });

          // Replace pending entries with uploaded metadata, keep already-uploaded ones
          finalConfig.attachments = finalConfig.attachments.map(att => {
            if (att.status === 'pending' && uploadMap.has(att.id)) {
              const up = uploadMap.get(att.id);
              return {
                attachmentId: up.attachmentId,
                storagePath: up.storagePath,
                filename: up.filename,
                mimeType: up.mimeType,
                size: up.size,
                source: 'outbound',
              };
            }
            // Already uploaded attachment — keep as-is (strip any non-serializable fields)
            const { file, status, id, ...rest } = att;
            return rest.attachmentId ? rest : att;
          });
        } catch (uploadErr) {
          console.error('Failed to upload email attachments:', uploadErr);
          throw new Error('Failed to upload attachments. Please try again.');
        }
      } else {
        // No pending files — strip non-serializable fields from all attachments
        finalConfig.attachments = finalConfig.attachments.map(att => {
          const { file, status, id, ...rest } = att;
          return rest.attachmentId ? rest : att;
        });
      }
    }

    const updatedWorkflowSteps = workflowSteps.map(step => {
      if (step.id !== stepId) return step;
      const updatedStep = { ...step, config: finalConfig };
      // Update both title and subtitle for the step
      const title = getStepTitle({ ...updatedStep });
      const subtitle = getStepSubtitle({ ...updatedStep, subtitle: undefined });
      return { ...updatedStep, title, subtitle };
    });

    // Persist only Firestore-ready steps (exclude trigger + UI-only fields)
    const firestoreSteps = updatedWorkflowSteps.slice(1).map(step => ({
      id: step.id,
      type: step.type,
      config: step.config || {}
    }));

    await updateAutomation(automationId, { steps: firestoreSteps });
    setWorkflowSteps(updatedWorkflowSteps);
  }, [automationId, workflowSteps, getStepTitle, getStepSubtitle, isReadOnly, showReadOnlyToast]);

  /**
   * REMOVE STEP - Deletes a step by its ID
   * 
   * @param {string} stepId - The unique ID of the step to remove
   * 
   * VALIDATION RULES:
   * - Cannot remove trigger step (workflow must have entry point)
   * - No-op if stepId doesn't exist
   * 
   * EXAMPLE:
   * removeStep('sms-1') with steps [trigger, sms-1, wait-1]
   * Results in: [trigger, wait-1]
   * 
   * CALLED BY (future):
   * - Step context menu "Delete" option
   * - Keyboard shortcut (Delete key when step selected)
   */
  const removeStep = useCallback((stepId) => {
    // GUARD: Read-only mode — automation is ACTIVE
    if (isReadOnly) { showReadOnlyToast(); return; }

    setWorkflowSteps(prevSteps => {
      // Find the step to remove
      const stepToRemove = prevSteps.find(s => s.id === stepId);

      // GUARD: Cannot remove trigger
      if (stepToRemove?.type === 'trigger') {
        console.warn('Cannot remove trigger step - workflow must have an entry point');
        return prevSteps; // Return unchanged
      }

      // GUARD: Step doesn't exist
      if (!stepToRemove) {
        console.warn(`Step with id '${stepId}' not found`);
        return prevSteps; // Return unchanged
      }

      // Filter out the step (immutable operation)
      return prevSteps.filter(step => step.id !== stepId);
    });

    // If the removed step was selected, close the config panel
    if (selectedNode === stepId) {
      closeConfig();
    }
  }, [selectedNode, closeConfig, isReadOnly, showReadOnlyToast]);

  /**
   * REORDER STEPS - Moves a step from one position to another
   * 
   * @param {number} fromIndex - Current position of the step
   * @param {number} toIndex - Desired new position
   * 
   * VALIDATION RULES:
   * - Cannot move trigger (fromIndex cannot be 0)
   * - Cannot move to trigger's position (toIndex cannot be 0)
   * - No-op if indices are equal or invalid
   * 
   * EXAMPLE:
   * reorderSteps(1, 3) with steps [trigger, A, B, C]
   * Results in: [trigger, B, C, A]
   * 
   * CALLED BY (future):
   * - WorkflowNode drag-to-reorder handler
   * - Keyboard arrow keys (when step selected)
   */
  const reorderSteps = useCallback((fromIndex, toIndex) => {
    // GUARD: Read-only mode — automation is ACTIVE
    if (isReadOnly) { showReadOnlyToast(); return; }

    // GUARD: Cannot move trigger
    if (fromIndex === 0) {
      console.warn('Cannot move trigger step - it must remain at position 0');
      return;
    }

    // GUARD: Cannot move to trigger's position
    if (toIndex === 0) {
      console.warn('Cannot move step to position 0 - trigger must remain first');
      return;
    }

    // GUARD: No-op if same position
    if (fromIndex === toIndex) {
      return;
    }

    setWorkflowSteps(prevSteps => {
      // GUARD: Invalid indices
      if (fromIndex < 0 || fromIndex >= prevSteps.length ||
        toIndex < 0 || toIndex >= prevSteps.length) {
        console.warn(`Invalid indices: from=${fromIndex}, to=${toIndex}, length=${prevSteps.length}`);
        return prevSteps;
      }

      // Create new array (immutable)
      const newSteps = [...prevSteps];

      // Remove the step from its current position
      const [movedStep] = newSteps.splice(fromIndex, 1);

      // Insert at the new position
      newSteps.splice(toIndex, 0, movedStep);

      return newSteps;
    });
  }, [isReadOnly, showReadOnlyToast]);

  // ========== STATUS TOGGLE HANDLER ==========
  // Toggles automation between ACTIVE and PAUSED status
  // For manual triggers, activation creates execution runs for selected leads
  const handleStatusToggle = useCallback(async (workflowId, isActive) => {
    try {
      // Optimistic update
      setWorkflows(prev =>
        prev.map(w =>
          w.id === workflowId
            ? { ...w, status: isActive ? 'ACTIVE' : 'PAUSED' }
            : w
        )
      );

      // Call API
      const result = await toggleAutomationStatus(workflowId, isActive);

      // Log execution info for manual triggers
      if (isActive && result?.executionsCreated > 0) {
        console.log(`[AutomationsContainer] Activation created ${result.executionsCreated} execution run(s) for selected leads`);
        // TODO: Show toast "Started X execution(s) for selected leads"
      }
    } catch (error) {
      console.error('Failed to toggle status:', error);
      // Revert on error
      setWorkflows(prev =>
        prev.map(w =>
          w.id === workflowId
            ? { ...w, status: isActive ? 'PAUSED' : 'ACTIVE' }
            : w
        )
      );
    }
  }, []);

  // ========== START AUTOMATION HANDLER ==========
  // Called when user clicks "Start Automation" button on the trigger node
  // Saves the automation first, then activates it, creating execution runs for manual triggers
  const handleStartAutomation = useCallback(async () => {
    if (!automationId) {
      console.error('[AutomationsContainer] Cannot start automation - no automation ID');
      return;
    }

    try {
      // First save the automation to persist latest changes
      console.log('[AutomationsContainer] Saving automation before activation...');
      await saveAutomation({ showSavingIndicator: false });

      // Then activate it (creates runs for manual triggers)
      console.log('[AutomationsContainer] Activating automation:', automationId);
      const result = await toggleAutomationStatus(automationId, true);
      console.log('[AutomationsContainer] Activation result:', result);

      // Update local state to reflect ACTIVE status
      setCurrentAutomation(prev => prev ? { ...prev, status: 'ACTIVE' } : prev);
      setWorkflows(prev =>
        prev.map(w =>
          w.id === automationId
            ? { ...w, status: 'ACTIVE' }
            : w
        )
      );

      // Log execution info based on trigger type
      if (result?.executionsResumed > 0) {
        console.log(`[AutomationsContainer] Resumed ${result.executionsResumed} previously paused lead(s)`);
      }
      if (result?.executionsCreated > 0) {
        console.log(`[AutomationsContainer] Automation activated - executing ${result.executionsCreated} selected lead(s)`);
        console.log('[AutomationsContainer] Execution IDs:', result.executionIds);
        // TODO: Show toast "Automation started with X lead(s)"
      } else if (triggerConfig.entryType === 'manual' && !result?.executionsResumed) {
        console.warn('[AutomationsContainer] Manual trigger but no executions created! Check selectedLeadIds:', triggerConfig.selectedLeadIds);
      } else {
        console.log('[AutomationsContainer] Automation activated - listening for form submissions');
        // TODO: Show toast "Automation is now active"
      }
    } catch (error) {
      console.error('[AutomationsContainer] Failed to start automation:', error);
      // TODO: Show error toast
    }
  }, [automationId, saveAutomation, triggerConfig]);

  // ========== STOP AUTOMATION HANDLER ==========
  // Called when user clicks "Stop" in builder toolbar
  // Hard stops automation and sets status to STOPPED
  const handleStopAutomation = useCallback(async () => {
    if (!automationId) {
      console.error('[AutomationsContainer] Cannot stop automation - no automation ID');
      return;
    }

    try {
      console.log('[AutomationsContainer] Stopping automation:', automationId);
      const result = await stopAutomation(automationId);
      console.log('[AutomationsContainer] Stop result:', result);

      // Update local + store state to reflect STOPPED status
      setCurrentAutomation(prev => prev ? { ...prev, status: 'STOPPED' } : prev);
      setWorkflows(prev =>
        prev.map(w =>
          w.id === automationId
            ? { ...w, status: 'STOPPED' }
            : w
        )
      );

      if (result?.executionsPaused > 0) {
        console.log(`[AutomationsContainer] Paused ${result.executionsPaused} execution(s) - leads preserved at current step`);
      } else {
        console.log('[AutomationsContainer] Automation stopped - no active executions');
      }
    } catch (error) {
      console.error('[AutomationsContainer] Failed to stop automation:', error);
      // TODO: Show error toast
    }
  }, [automationId]);

  // ========== TEST RUN HANDLER ==========
  // Called when Test Run modal submits with test email/phone
  // Executes all steps immediately without creating execution records
  const handleTestRun = useCallback(async (testContact) => {
    if (!automationId) {
      throw new Error('Cannot run test - automation not saved');
    }

    console.log('[AutomationsContainer] Running test with:', testContact);
    const result = await runAutomationTest(automationId, testContact);
    console.log('[AutomationsContainer] Test run result:', result);
    return result;
  }, [automationId]);

  // ========== OPEN TEST RUN MODAL ==========
  const handleOpenTestRunModal = useCallback(() => {
    setShowTestRunModal(true);
  }, []);

  // ========== TRIGGER CONFIG CHANGE HANDLER ==========
  // Called by TriggerConfigContent when Add Manually selections change
  // Updates both the triggerConfig state AND syncs to workflowSteps[0].config
  // This ensures the Start Trigger node displays current config values
  const handleTriggerConfigChange = useCallback((newConfig) => {
    // GUARD: Read-only mode — automation is ACTIVE
    if (isReadOnly) { showReadOnlyToast(); return; }

    setTriggerConfig(newConfig);

    // Sync to trigger step in workflowSteps for UI display
    setWorkflowSteps(prev => {
      if (prev.length === 0) return prev;
      const triggerStep = prev[0];
      if (triggerStep.type !== 'trigger') return prev;

      // Merge new config into trigger step
      return [
        {
          ...triggerStep,
          config: {
            ...triggerStep.config,
            ...newConfig,
          },
        },
        ...prev.slice(1),
      ];
    });
  }, [isReadOnly, showReadOnlyToast]);

  if (isMobile) {
    return (
      <div className="flex flex-col h-full overflow-hidden bg-stone-50">
        {currentView === 'library' && (
          <AutomationLibrary
            activeLibTab={activeLibTab}
            setActiveLibTab={setActiveLibTab}
            workflows={workflows}
            workflowMetricsMap={workflowMetricsMap}
            pausedLeads={filteredAttentionRows}
            totalPausedCount={normalizedAttentionRows.length}
            pausedLeadsLoading={attentionLoading}
            attentionFilter={attentionFilter}
            onAttentionFilterChange={setAttentionFilter}
            onResumeRun={handleResumeRun}
            onCreateNew={handleCreateNew}
            onCardClick={handleCardClick}
            onStatusToggle={handleStatusToggle}
            onDeleteAutomation={handleRequestDeleteAutomation}
            isLoading={isLoadingWorkflows}
            isMobile={isMobile}
          />
        )}

        {currentView === 'builder' && (
          <AutomationBuilder
            currentAutomation={currentAutomation}
            workflowSteps={workflowSteps}
            selectedNode={selectedNode}
            selectedStep={selectedStep}
            showLeadsPanel={showLeadsPanel}
            showConfigPanel={showConfigPanel}
            configType={configType}
            // ========== LEAD MONITOR DATA (Real-time from Firestore) ==========
            automationRuns={automationRuns}
            runsLoading={runsLoading}
            runStats={runStats}
            onPauseRun={handlePauseRun}
            onResumeRun={handleResumeRun}
            onStopRun={handleStopRun}
            onDeleteRun={handleDeleteRun}
            // ========== LEAD MONITOR FILTERS ==========
            monitorStatusFilter={monitorStatusFilter}
            monitorSourceFilter={monitorSourceFilter}
            onStatusFilterChange={setMonitorStatusFilter}
            onSourceFilterChange={setMonitorSourceFilter}
            onBack={handleBack}
            onNodeClick={handleNodeClick}
            onToggleLeadsPanel={handleToggleLeadsPanel}
            onShowGuardrails={handleShowGuardrails}
            onCloseLeadsPanel={handleCloseLeadsPanel}
            onCloseConfig={closeConfig}
            onSaveNodeConfig={handleSaveNodeConfig}
            // ========== STEP MANIPULATION HANDLERS ==========
            // These handlers enable dynamic workflow modification.
            // Currently wired but not triggered (no drag UI yet).
            // Will be called by drag-and-drop in next implementation phase.
            onAddStep={addStep}
            onRemoveStep={removeStep}
            onReorderSteps={reorderSteps}
            // ========== SAVE HANDLERS ==========
            onSave={saveAutomation}
            onNameChange={handleNameChange}
            isSaving={isSaving}
            // ========== TRIGGER CONFIG (Add Manually) ==========
            leads={leads}
            leadsLoading={leadsLoading}
            triggerConfig={triggerConfig}
            onTriggerConfigChange={handleTriggerConfigChange}
            forms={availableForms}
            // ========== START/STOP AUTOMATION ==========
            onStartAutomation={handleStartAutomation}
            onStopAutomation={handleStopAutomation}
            onTestRun={handleOpenTestRunModal}
            isMobile={isMobile}
            isReadOnly={isReadOnly}
          />
        )}

        <GuardrailsModal
          showGuardrails={showGuardrails}
          onClose={handleCloseGuardrails}
          onSave={handleSaveGuardrails}
          guardrails={guardrails}
          isMobile={isMobile}
          isReadOnly={isReadOnly}
        />

        <TestRunModal
          isOpen={showTestRunModal}
          onClose={() => setShowTestRunModal(false)}
          onRunTest={handleTestRun}
          automationName={currentAutomation?.name}
        />

        {/* Delete Automation Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={handleCancelDeleteAutomation}>
            <div className="bg-white rounded-xl p-6 max-w-sm w-full mx-4 shadow-xl" onClick={e => e.stopPropagation()}>
              <h3 className="text-lg font-bold text-stone-800 mb-2">Delete Automation?</h3>
              <p className="text-sm text-stone-500 mb-6">
                This will permanently delete this automation and all its execution history, tasks, and logs. This action cannot be undone.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={handleCancelDeleteAutomation}
                  className="px-4 py-2 text-sm font-medium text-stone-600 bg-stone-100 hover:bg-stone-200 rounded-lg transition"
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDeleteAutomation}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition flex items-center gap-2"
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Read-Only Toast */}
        {readOnlyToast && (
          <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[100] bg-amber-600 text-white px-5 py-3 rounded-xl shadow-lg text-sm font-medium flex items-center gap-2 animate-fade-in">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v.01M12 9v3m0 0a9 9 0 110 18 9 9 0 010-18z" /></svg>
            Stop the automation before making changes.
          </div>
        )}
      </div>
    );
  }

  // Desktop View
  return (
    <div className="flex flex-col h-full bg-white relative overflow-hidden">
      {currentView === 'library' && (
        <AutomationLibrary
          activeLibTab={activeLibTab}
          setActiveLibTab={setActiveLibTab}
          workflows={workflows}
          workflowMetricsMap={workflowMetricsMap}
          pausedLeads={filteredAttentionRows}
          totalPausedCount={normalizedAttentionRows.length}
          pausedLeadsLoading={attentionLoading}
          attentionFilter={attentionFilter}
          onAttentionFilterChange={setAttentionFilter}
          onResumeRun={handleResumeRun}
          onCreateNew={handleCreateNew}
          onCardClick={handleCardClick}
          onStatusToggle={handleStatusToggle}
          onDeleteAutomation={handleRequestDeleteAutomation}
          isLoading={isLoadingWorkflows}
          isMobile={isMobile}
        />
      )}

      {currentView === 'builder' && (
        <AutomationBuilder
          currentAutomation={currentAutomation}
          workflowSteps={workflowSteps}
          selectedNode={selectedNode}
          selectedStep={selectedStep}
          showLeadsPanel={showLeadsPanel}
          showConfigPanel={showConfigPanel}
          configType={configType}
          // ========== LEAD MONITOR DATA (Real-time from Firestore) ==========
          automationRuns={automationRuns}
          runsLoading={runsLoading}
          runStats={runStats}
          onPauseRun={handlePauseRun}
          onResumeRun={handleResumeRun}
          onStopRun={handleStopRun}
          onDeleteRun={handleDeleteRun}
          // ========== LEAD MONITOR FILTERS ==========
          monitorStatusFilter={monitorStatusFilter}
          monitorSourceFilter={monitorSourceFilter}
          onStatusFilterChange={setMonitorStatusFilter}
          onSourceFilterChange={setMonitorSourceFilter}
          onBack={handleBack}
          onNodeClick={handleNodeClick}
          onToggleLeadsPanel={handleToggleLeadsPanel}
          onShowGuardrails={handleShowGuardrails}
          onCloseLeadsPanel={handleCloseLeadsPanel}
          onCloseConfig={closeConfig}
          onSaveNodeConfig={handleSaveNodeConfig}
          // ========== STEP MANIPULATION HANDLERS ==========
          // These handlers enable dynamic workflow modification.
          // Currently wired but not triggered (no drag UI yet).
          // Will be called by drag-and-drop in next implementation phase.
          onAddStep={addStep}
          onRemoveStep={removeStep}
          onReorderSteps={reorderSteps}
          // ========== SAVE HANDLERS ==========
          onSave={saveAutomation}
          onNameChange={handleNameChange}
          isSaving={isSaving}
          // ========== TRIGGER CONFIG (Add Manually) ==========
          leads={leads}
          leadsLoading={leadsLoading}
          triggerConfig={triggerConfig}
          onTriggerConfigChange={handleTriggerConfigChange}
          forms={availableForms}
          // ========== START/STOP AUTOMATION ==========
          onStartAutomation={handleStartAutomation}
          onStopAutomation={handleStopAutomation}
          onTestRun={handleOpenTestRunModal}
          isMobile={isMobile}
          isReadOnly={isReadOnly}
        />
      )}

      <GuardrailsModal
        showGuardrails={showGuardrails}
        onClose={handleCloseGuardrails}
        onSave={handleSaveGuardrails}
        guardrails={guardrails}
        isMobile={isMobile}
        isReadOnly={isReadOnly}
      />

      <TestRunModal
        isOpen={showTestRunModal}
        onClose={() => setShowTestRunModal(false)}
        onRunTest={handleTestRun}
        automationName={currentAutomation?.name}
      />

      {/* Delete Automation Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={handleCancelDeleteAutomation}>
          <div className="bg-white rounded-xl p-6 max-w-sm w-full mx-4 shadow-xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-stone-800 mb-2">Delete Automation?</h3>
            <p className="text-sm text-stone-500 mb-6">
              This will permanently delete this automation and all its execution history, tasks, and logs. This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={handleCancelDeleteAutomation}
                className="px-4 py-2 text-sm font-medium text-stone-600 bg-stone-100 hover:bg-stone-200 rounded-lg transition"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDeleteAutomation}
                className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition flex items-center gap-2"
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Read-Only Toast */}
      {readOnlyToast && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[100] bg-amber-600 text-white px-5 py-3 rounded-xl shadow-lg text-sm font-medium flex items-center gap-2 animate-fade-in">
          <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v.01M12 9v3m0 0a9 9 0 110 18 9 9 0 010-18z" /></svg>
          Stop the automation before making changes.
        </div>
      )}
    </div>
  );
};

export default AutomationsContainer;
